import { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { MdOutlineTableChart, MdOutlineBarChart } from "react-icons/md";
import {
  SERIES,
  TEXTO_EIXO,
  baseChartOptions,
  barPlotOptions,
  barDataLabels,
} from "./chartTheme";
import { formatMoney, formatNumber, formatPercent } from "./crmHelpers";

/*
  Gráficos do funil.

  Cada um substitui uma TABELA que estava aqui antes. A troca não é estética:
  a tabela de canais tinha sete colunas, e comparar "40% contra 85,7% contra
  100%" lendo números em linhas separadas é exatamente o trabalho que uma
  barra faz de graça. O que a tabela fazia melhor — o valor exato — continua
  disponível no botão "ver números".
*/

const Alternador = ({ modo, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:border-pink-200 hover:text-pink-700"
  >
    {modo === "grafico" ? (
      <>
        <MdOutlineTableChart size={14} /> ver números
      </>
    ) : (
      <>
        <MdOutlineBarChart size={14} /> ver gráfico
      </>
    )}
  </button>
);

/* ------------------------------------------------------------------ canais */

/**
 * Conversão por canal, em barras horizontais.
 *
 * Horizontal porque os rótulos são nomes ("Loja de apps", "Não informado"):
 * na vertical eles giram 45° ou somem. Ordenado por conversão, não por
 * volume — a pergunta é qual canal CONVERTE, e a barra de volume ao lado
 * impede a leitura ingênua de que um canal com 1 cadastro e 100% é o melhor.
 */
export const CanaisChart = ({ bySource = [] }) => {
  const [modo, setModo] = useState("grafico");

  if (!bySource.length) {
    return (
      <p className="px-4 py-8 text-center text-sm text-slate-400">
        Nenhum cadastro no período.
      </p>
    );
  }

  const ordenado = [...bySource].sort((a, b) => b.conversionRate - a.conversionRate);
  /*
    O volume entra no rótulo do EIXO, não no rótulo de dado. Com os dois
    juntos na ponta da barra ("15% · 20 cadastros"), o texto começava antes do
    fim da barra e ficava por cima dela — ilegível justamente nos canais de
    maior conversão, que são os que importam.

    E o volume ao lado do nome resolve a leitura ingênua: 100% de 1 cadastro
    não é um canal melhor que 28% de 25.
  */
  const categorias = ordenado.map(
    (item) =>
      `${item.label} · ${formatNumber(item.signups)} · ${formatPercent(
        item.conversionRate
      )}`
  );

  const options = {
    ...baseChartOptions(categorias, {
      xaxis: {
        labels: {
          style: { colors: TEXTO_EIXO, fontSize: "11px" },
          formatter: (value) => `${Math.round(value)}%`,
        },
      },
      tooltip: { shared: false, intersect: true },
    }),
    plotOptions: barPlotOptions(true),
    colors: [SERIES.primaria],
    yaxis: {
      labels: {
        style: { colors: TEXTO_EIXO, fontSize: "11px" },
        // Espaço para "Não informado · 3 · 0%" sem reticências.
        maxWidth: 210,
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
      style: { fontSize: "12px", fontFamily: "inherit" },
      y: {
        formatter: (value, { dataPointIndex }) => {
          const item = ordenado[dataPointIndex];
          return `${formatPercent(value)} — ${formatNumber(
            item.everPaid
          )} de ${formatNumber(item.signups)} pagaram`;
        },
      },
    },
    // Espaço à direita para o rótulo direto não ser cortado.
    grid: { ...baseChartOptions(categorias).grid, padding: { right: 16 } },
  };

  return (
    <div className="p-4">
      <div className="mb-2 flex items-center justify-end">
        <Alternador
          modo={modo}
          onToggle={() => setModo((v) => (v === "grafico" ? "tabela" : "grafico"))}
        />
      </div>

      {modo === "tabela" ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-2">Canal</th>
                <th className="py-2 px-2 text-right">Cadastros</th>
                <th className="py-2 px-2 text-right">Ativação</th>
                <th className="py-2 px-2 text-right">Pagaram</th>
                <th className="py-2 px-2 text-right">Conversão</th>
                <th className="py-2 pl-2 text-right">MRR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ordenado.map((item) => (
                <tr key={item.source}>
                  <td className="py-1.5 pr-2 text-slate-700">{item.label}</td>
                  <td className="py-1.5 px-2 text-right font-semibold text-slate-800">
                    {formatNumber(item.signups)}
                  </td>
                  <td className="py-1.5 px-2 text-right text-slate-600">
                    {formatPercent(item.activationRate)}
                  </td>
                  <td className="py-1.5 px-2 text-right font-semibold text-slate-800">
                    {formatNumber(item.everPaid)}
                  </td>
                  <td className="py-1.5 px-2 text-right font-semibold text-slate-800">
                    {formatPercent(item.conversionRate)}
                  </td>
                  <td className="py-1.5 pl-2 text-right text-slate-600">
                    {formatMoney(item.mrr)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ReactApexChart
          type="bar"
          height={Math.max(ordenado.length * 46, 160)}
          options={options}
          series={[
            { name: "Conversão", data: ordenado.map((item) => item.conversionRate) },
          ]}
        />
      )}
    </div>
  );
};

/* ----------------------------------------------------------------- coortes */

/**
 * Coortes mensais: quantas contas entraram e quantas delas pagaram.
 *
 * Duas séries de CONTAGEM, mesmo eixo — a conversão em % fica no tooltip, não
 * numa segunda escala. Dois eixos aqui fariam a linha de conversão cruzar a de
 * cadastros em pontos definidos pela escala, não pelos dados.
 */
export const CoortesChart = ({ byMonth = [] }) => {
  const [modo, setModo] = useState("grafico");

  if (!byMonth.length) {
    return (
      <p className="px-4 py-8 text-center text-sm text-slate-400">
        Sem cadastros no período.
      </p>
    );
  }

  // Últimos 18 meses: além disso o eixo vira uma parede e a leitura some.
  const serie = byMonth.slice(-18);
  const categorias = serie.map((item) => item.month);

  const options = {
    ...baseChartOptions(categorias),
    colors: [SERIES.primaria, SERIES.secundaria],
    plotOptions: barPlotOptions(),
    dataLabels: barDataLabels((value) => (value > 0 ? value : "")),
    chart: {
      ...baseChartOptions(categorias).chart,
      stacked: false,
    },
    tooltip: {
      shared: true,
      intersect: false,
      style: { fontSize: "12px", fontFamily: "inherit" },
      y: { formatter: (value) => formatNumber(value) },
    },
  };

  return (
    <div className="p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {[
            { label: "Cadastraram", color: SERIES.primaria },
            { label: "Pagaram ao menos uma vez", color: SERIES.secundaria },
          ].map((item) => (
            <span
              key={item.label}
              className="flex items-center gap-1.5 text-[11px] text-slate-600"
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.label}
            </span>
          ))}
        </div>
        <Alternador
          modo={modo}
          onToggle={() => setModo((v) => (v === "grafico" ? "tabela" : "grafico"))}
        />
      </div>

      {modo === "tabela" ? (
        <div className="max-h-[300px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-2">Mês</th>
                <th className="py-2 px-2 text-right">Cadastros</th>
                <th className="py-2 px-2 text-right">Ativação</th>
                <th className="py-2 pl-2 text-right">Conversão</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[...serie].reverse().map((item) => (
                <tr key={item.month}>
                  <td className="py-1.5 pr-2 text-slate-600">{item.month}</td>
                  <td className="py-1.5 px-2 text-right font-semibold text-slate-800">
                    {formatNumber(item.signups)}
                  </td>
                  <td className="py-1.5 px-2 text-right text-slate-600">
                    {formatPercent(item.activationRate)}
                  </td>
                  <td className="py-1.5 pl-2 text-right font-semibold text-slate-800">
                    {formatPercent(item.conversionRate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ReactApexChart
          type="bar"
          height={230}
          options={options}
          series={[
            { name: "Cadastraram", data: serie.map((item) => item.signups) },
            { name: "Pagaram", data: serie.map((item) => item.everPaid) },
          ]}
        />
      )}
    </div>
  );
};

/* -------------------------------------------------------------- onboarding */

/** Sessões que chegaram a cada tela do cadastro. */
export const OnboardingChart = ({ steps = [] }) => {
  if (!steps.length) return null;

  const categorias = steps.map((item) => item.stepId);

  const options = {
    ...baseChartOptions(categorias, {
      xaxis: {
        labels: {
          style: { colors: TEXTO_EIXO, fontSize: "10px" },
          rotate: -35,
          hideOverlappingLabels: false,
          trim: true,
          maxHeight: 72,
        },
      },
    }),
    plotOptions: barPlotOptions(),
    dataLabels: barDataLabels((value) => value),
    colors: [SERIES.primaria],
    tooltip: {
      shared: false,
      intersect: true,
      style: { fontSize: "12px", fontFamily: "inherit" },
      y: {
        formatter: (value, { dataPointIndex }) => {
          const item = steps[dataPointIndex];
          return `${formatNumber(value)} sessões · ${formatPercent(
            item.shareOfEntry
          )} da entrada${
            item.dropFromPrevious
              ? ` · perdeu ${formatNumber(item.dropFromPrevious)} na tela anterior`
              : ""
          }`;
        },
      },
    },
  };

  return (
    <ReactApexChart
      type="bar"
      height={240}
      options={options}
      series={[{ name: "Sessões", data: steps.map((item) => item.sessions) }]}
    />
  );
};
